import { useReducer, useEffect, useMemo } from 'react';
// We need the main context to access the account state
// import { useContextState } from './Context';
import { inboxReducer, inboxFilterReducer } from './Reducers'; // Assuming inboxFilterReducer now exists
import { Inbox } from '../models'; // Import the specific Inbox model type

// --- TYPE DEFINITIONS ---
const VALID_INBOX_STATES = ['supportUser'] as const;
export type InboxStateCategory = typeof VALID_INBOX_STATES[number];

export interface InboxDataState {
    inboxState: InboxStateCategory;
    // privateInboxState: Inbox[] | null;
    // eventsInboxState: Inbox[] | null;
    supportUserInboxState: Inbox[] | null;
}

export interface InboxFilterState {
    sort: string;
    searchQuery: string;
}

// --- TYPE GUARD FUNCTION ---
export function isValidInboxState(value: any): value is InboxStateCategory {
    return VALID_INBOX_STATES.includes(value);
}

// --- Default State Definitions ---
const defaultInboxState: InboxDataState = {
    inboxState: 'supportUser',
    // privateInboxState: null,
    // eventsInboxState: null,
    supportUserInboxState: null,
};

export const defaultInboxFilterState: InboxFilterState = {
    sort: 'soonToLater',
    searchQuery: "",
};

export const useInboxContext = (signedIn?: boolean) => {
    // --- Access AccountContext to check login status ---
    // const { accountCTX } = useContextState() || {};
    // const signedIn = accountCTX?.signedIn;

    // --- Initialize state with SERVER-SAFE default values ---
    const [inboxState, dispatchInboxState] = useReducer(inboxReducer, defaultInboxState);
    // CRITICAL FIX: Use the correct reducer for inbox filters.
    const [inboxFilterState, inboxFilterDispatch] = useReducer(inboxFilterReducer, defaultInboxFilterState);

    // --- HYDRATION: Load state from localStorage only for a logged-in user ---
    useEffect(() => {
        if (signedIn) {
            const storedInboxState = window.localStorage.getItem("inboxState");
            if (isValidInboxState(storedInboxState)) {
                dispatchInboxState({ type: 'SET_INBOX_STATE', payload: storedInboxState });
            }
            
            const storedFiltersJSON = window.localStorage.getItem('inboxFilterState');
            if (storedFiltersJSON) {
                try {
                    const storedFilters = JSON.parse(storedFiltersJSON);
                    inboxFilterDispatch({ type: 'INITIALIZE_FILTERS', payload: storedFilters });
                } catch (error) {
                    console.error("Failed to parse stored inbox filters:", error);
                }
            }
        }
    }, [signedIn]);

    // --- PERSISTENCE: Save state back to localStorage only for a logged-in user ---
    useEffect(() => {
        if (signedIn && inboxState.inboxState) {
            window.localStorage.setItem("inboxState", inboxState.inboxState);
        }
    }, [signedIn, inboxState.inboxState]);
    
    useEffect(() => {
        if (signedIn) {
            try {
                window.localStorage.setItem('inboxFilterState', JSON.stringify(inboxFilterState));
            } catch (error) {
                console.error("Could not save inbox filter state:", error);
            }
        }
    }, [signedIn, inboxFilterState]);

    // --- NEW: AUTO-RESET ON LOGOUT ---
    useEffect(() => {
        if (signedIn === false) { // Explicitly check for false to avoid running on initial undefined state
            // Dispatch actions to reset both state slices to their defaults
            dispatchInboxState({ type: 'RESET_STATE' });
            inboxFilterDispatch({ type: 'RESET_FILTERS' });

            // Thoroughly clean up this context's localStorage items
            window.localStorage.removeItem("inboxState");
            window.localStorage.removeItem("inboxFilterState");
        }
    }, [signedIn]);

    const contextValue = useMemo(() => ({
        inboxState, 
        dispatchInboxState, 
        inboxFilterState, 
        inboxFilterDispatch
    }), [
        inboxState, 
        dispatchInboxState, 
        inboxFilterState, 
        inboxFilterDispatch
    ]);

    return contextValue;

    // return { inboxState, dispatchInboxState, inboxFilterState, inboxFilterDispatch };
}

export type InboxContextValue = ReturnType<typeof useInboxContext>;